import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { DataLayerTypeNameComponent } from '../data-layer-type-name/data-layer-type-name.component';
import { MultiSelectModule } from 'primeng/multiselect';
import { NgClass, NgIf } from '@angular/common';
import { PrimeTemplate } from 'primeng/api';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { DataLayerType } from '../../../models/data-layer-type';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { HeaderComponent } from '../header/header.component';

@Component({
  selector: 'app-type-multi-select',
  standalone: true,
  imports: [
    DataLayerTypeNameComponent,
    ReactiveFormsModule,
    MultiSelectModule,
    NgIf,
    PrimeTemplate,
    FormsModule,
    HeaderComponent,
    NgClass,
  ],
  templateUrl: './type-multi-select.component.html',
  styleUrl: './type-multi-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeMultiSelectComponent),
      multi: true,
    },
  ],
})
export class TypeMultiSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  readonly dataStore = inject(IndicatorDataStore);
  readonly layerTypeStore = inject(DataLayerTypeStore);
  readonly layerStore = inject(DataLayerStore);

  touched = false;
  disabled = false;
  selection: DataLayerType[] = [];
  typeSelectItems: DataLayerType[] = [];

  @Input() header?: { icon: string; text: string };
  @Input() label = 'Räume/ Standorte';
  @Input() year?: number | string = undefined;
  @Input() name = 'types';
  @Input() disableTypes?: string[];
  @Input() mode: 'all' | 'Point' | 'Polygon' = 'Polygon';
  @Output() selectionChange = new EventEmitter<DataLayerType[] | undefined>();

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * This implementation invokes the `setTypeSelectItems` method to prepare select items for the component.
   *
   * @return {void} Does not return any value.
   */
  ngOnInit(): void {
    this.setTypeSelectItems();
  }

  /**
   * A lifecycle hook method in Angular that is called when any data-bound property of the component changes.
   * It is used to respond to changes to input properties.
   *
   * @param {SimpleChanges} changes An object of current and previous property values, which indicates the changes to the input-bound properties of the component.
   * @return {void} Does not return a value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disableTypes']) this.setTypeSelectItems();
  }

  /**
   * Sets the `typeSelectItems` property based on the current mode and year.
   * Filters the available types from the `layerTypeStore` and assigns them to `typeSelectItems`.
   * The filtering is performed depending on the `mode` (Polygon, Point, or all) and ensures types are relevant to the specified year, if any.
   *
   * @return {void} This method does not return a value.
   */
  setTypeSelectItems(): void {
    let types: DataLayerType[] = [];
    switch (this.mode) {
      case 'Polygon':
        types = this.layerTypeStore.getByType('Polygon');
        this.label = 'Räume';
        break;
      case 'Point':
        types = this.layerTypeStore.getByType('Point');
        this.label = 'Standorte';
        break;
      case 'all':
        types = this.layerTypeStore.asArray();
        break;
    }

    const year = this.year;
    if (year) {
      types = [...types].filter(type =>
        this.layerStore.parentHasYear(type._id, Number(year))
      );
    }

    if (this.disableTypes && this.disableTypes.length) {
      this.typeSelectItems = [...types].filter(
        type => this.disableTypes && !this.disableTypes.includes(type._id)
      );
    } else {
      this.typeSelectItems = types;
    }
  }

  /**
   * A callback function triggered when there is a change in the selection.
   *
   * @param {DataLayerType[] | undefined} value - The new selection value, which can be an array
   * of DataLayerType or undefined if no value is selected.
   */
  protected onChange = (value: DataLayerType[] | undefined) => {
    this.selectionChange.emit(value);
  };

  private onTouched = () => {
    this.touched = false;
  };

  /**
   * Writes the provided value to the selection property.
   *
   * @param {any} value - The value to be written to the selection property.
   * @return {void} This method does not return a value.
   */
  writeValue(value: any): void {
    this.selection = value;
  }

  /**
   * Registers a callback function to be called when a change event occurs.
   *
   * @param {any} fn The callback function to handle change events.
   * @return {void} This method does not return any value.
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function that will be invoked when the component is touched.
   *
   * @param {any} fn - The callback function to register. This function should handle touch events for the component.
   * @return {void} This method does not return a value.
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Updates the disabled state of the component.
   *
   * @param {boolean} isDisabled - A flag indicating whether the component should be disabled.
   * @return {void} This method does not return a value.
   */
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
